import React, { useEffect, useState } from 'react';
import {
    Network,
    RefreshCw,
    Router,
    RotateCw,
    Box,
    Settings,
    HelpCircle,
    ChevronDown,
    ChevronUp,
    AlertCircle
} from 'lucide-react';
import { LysepAPI } from '../../../contexts/ApiContext';
import { toast } from 'react-toastify';

const InterfacesTab = ({ serverId }) => {
    const [interfaces, setInterfaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetching, setFetching] = useState(false);
    const [error, setError] = useState(null);
    const [expandedCategories, setExpandedCategories] = useState(['physical']);

    const loadInterfaces = async () => {
        setLoading(true);
        setError(null);
        try {
            const resp = await LysepAPI.get(`/api/servers/${serverId}/ssh/interfaces`);
            if (resp.data.state) {
                setInterfaces(resp.data.data);
            } else {
                setError(resp.data.message);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchInterfaces = async () => {
        setFetching(true);
        const toastId = toast.loading('Ağ arayüzleri SSH ile çekiliyor...');
        try {
            const resp = await LysepAPI.post(`/api/servers/${serverId}/ssh/interfaces/fetch`);
            if (resp.data.state) {
                setInterfaces(resp.data.data);
                toast.update(toastId, { render: 'Başarıyla güncellendi!', type: "success", isLoading: false, autoClose: 3000 });
            } else {
                toast.update(toastId, { render: `Hata: ${resp.data.message}`, type: "error", isLoading: false, autoClose: 4000 });
            }
        } catch (err) {
            toast.update(toastId, { render: `Hata: ${err.message}`, type: "error", isLoading: false, autoClose: 4000 });
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        loadInterfaces();
    }, [serverId]);

    const toggleCategory = (category) => {
        setExpandedCategories(prev =>
            prev.includes(category)
                ? prev.filter(c => c !== category)
                : [...prev, category]
        );
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center p-12 text-gray-400">
                <RefreshCw className="w-8 h-8 animate-spin mb-3 text-blue-500" />
                <p>Yükleniyor...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-xl flex items-center gap-3">
                <AlertCircle className="w-5 h-5" />
                <p>Hata: {error}</p>
            </div>
        );
    }

    const getInterfaceCategory = (name) => {
        if (name === 'lo') return 'loopback';
        if (name.startsWith('eth') || name.startsWith('en') || name.startsWith('wl') || name.startsWith('wlan')) return 'physical';
        if (name.startsWith('docker') || name.startsWith('veth') || name.startsWith('br-')) return 'docker';
        if (name.startsWith('br') || name.startsWith('virbr') || name.startsWith('tun') || name.startsWith('tap')) return 'virtual';
        return 'other';
    };

    const categoryConfig = {
        physical: { label: 'Fiziksel Arayüzler', icon: Router, color: 'text-blue-400' },
        loopback: { label: 'Loopback', icon: RotateCw, color: 'text-gray-400' },
        docker: { label: 'Docker & Konteyner', icon: Box, color: 'text-cyan-400' },
        virtual: { label: 'Etkileşimli & Sanal', icon: Settings, color: 'text-yellow-400' },
        other: { label: 'Diğer', icon: HelpCircle, color: 'text-gray-400' }
    };

    // Group interfaces
    const groupedInterfaces = interfaces.reduce((acc, iface) => {
        if (!acc.byName[iface.interface_name]) {
            acc.byName[iface.interface_name] = {
                name: iface.interface_name,
                mac: iface.mac_address,
                status: iface.status,
                ips: [],
                category: getInterfaceCategory(iface.interface_name)
            };
        }
        if (iface.ip_address) {
            acc.byName[iface.interface_name].ips.push({
                address: iface.ip_address,
                family: iface.ip_family,
                prefix: iface.prefix_length,
                isPublic: iface.is_public
            });
        }
        return acc;
    }, { byName: {} });

    const categorizedInterfaces = Object.values(groupedInterfaces.byName).reduce((acc, iface) => {
        if (!acc[iface.category]) acc[iface.category] = [];
        acc[iface.category].push(iface);
        return acc;
    }, {});

    const categoryOrder = ['physical', 'virtual', 'docker', 'loopback', 'other'];

    return (
        <div className="bg-[#1a1a1a] border border-white/10 rounded-xl overflow-hidden shadow-lg animate-fadeIn">
            <div className="p-4 border-b border-white/10 bg-[#252525] flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Network className="w-5 h-5 text-gray-400" />
                    <h3 className="font-semibold text-white uppercase tracking-wider text-sm">Network Interfaces</h3>
                </div>
                <button
                    onClick={fetchInterfaces}
                    disabled={fetching}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-300 hover:text-white border border-white/10 hover:border-white/20 rounded-lg transition-colors bg-[#1e1e1e] hover:bg-[#2a2a2a] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${fetching ? 'animate-spin' : ''}`} />
                    {fetching ? 'Yenileniyor...' : 'Yenile'}
                </button>
            </div>

            <div className="p-4 space-y-2">
                {categoryOrder.map(catKey => {
                    const categoryItems = categorizedInterfaces[catKey];
                    if (!categoryItems || categoryItems.length === 0) return null;

                    const config = categoryConfig[catKey];
                    const Icon = config.icon;
                    const isExpanded = expandedCategories.includes(catKey);

                    return (
                        <div key={catKey} className="border border-white/10 rounded-lg overflow-hidden bg-[#1e1e1e]">
                            <button
                                onClick={() => toggleCategory(catKey)}
                                className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors"
                            >
                                <div className="flex items-center gap-2">
                                    <Icon className={`w-4 h-4 ${config.color}`} />
                                    <span className="text-sm font-semibold text-white">{config.label}</span>
                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-white/10 text-gray-300">
                                        {categoryItems.length}
                                    </span>
                                </div>
                                {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                            </button>

                            {isExpanded && (
                                <div className="border-t border-white/10 divide-y divide-white/5">
                                    {categoryItems.map((iface, idx) => (
                                        <div key={idx} className="p-3 hover:bg-[#252525] transition-colors">
                                            <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
                                                <div className="sm:col-span-3">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="font-mono font-medium text-blue-300">{iface.name}</span>
                                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${iface.status === 'UP' ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-gray-500/10 border-gray-500/20 text-gray-400'}`}>
                                                            {iface.status}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="sm:col-span-9 space-y-2">
                                                    <div>
                                                        <span className="text-[10px] text-gray-500 uppercase tracking-wider block mb-0.5">MAC Address</span>
                                                        <span className="font-mono text-xs text-gray-300">{iface.mac || 'N/A'}</span>
                                                    </div>

                                                    {iface.ips.length > 0 ? (
                                                        <div className="space-y-1">
                                                            {iface.ips.map((ip, ipIdx) => (
                                                                <div key={ipIdx} className="flex items-center gap-2">
                                                                    <div className="flex-1">
                                                                        <span className="text-[10px] text-gray-500 uppercase tracking-wider mr-2">
                                                                            {ip.family === 'inet' ? 'IPv4' : 'IPv6'}
                                                                        </span>
                                                                        <span className="font-mono text-sm text-white">
                                                                            {ip.address}<span className="text-gray-500">/{ip.prefix}</span>
                                                                        </span>
                                                                    </div>
                                                                    {ip.isPublic && (
                                                                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                                                            Public
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-gray-500 italic block mt-1">IP adresi yok</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default InterfacesTab;
